import { AgGridAngular } from '@ag-grid-community/angular';
import { ColDef } from '@ag-grid-community/core';
import { DOCUMENT, DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Component as BrComponent, Page } from '@bloomreach/spa-sdk/';
import {
  AnalyticsService,
  DialogService,
  DynamicTableColDef,
  FrkBreakpoint,
  ModalService,
  NotificationOptions,
  PaginationComponent,
  ResponsiveService,
  ToggleButton,
} from '@frk/eds-components';
import { DownloadLink } from '@literature/literature-fund-document/literature-fund-document.component';
import { LiteratureListingBaseComponent } from '@literature/literature-listing-base.component';
import {
  detailsGenerator,
  fundNameGenerator,
  nameGenerator,
} from '@literature/literature-utils';
import { CartHandlerService } from '@literature/services/cart-handler.service';
import { DownloadService } from '@literature/services/download.service';
import { LiteratureAgGridHelperService } from '@literature/services/literature-ag-grid-helper.service';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { FundFilterService } from '@products/fund-listing/services/fund-filter.service';
import { ProfileService, StorageService, WindowScrollService } from '@services';
import { TranslateService } from '@shared/translate/translate.service';
import {
  CustomLiteratureDocumentListingData,
  ExcelDocType,
  FiltersState,
  LiteratureDocumentListingData,
  ModalId,
  SelectNotifications,
  SegmentId,
  LiteraturePageType,
  ShareclassLevelLiteratureDocumentListingData,
} from '@types';
import { Logger } from '@utils/logger';
import { createDateComparator, stringCompare } from '@utils/sort-utils';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LiteratureDocumentListingService } from './services/literature-document-listing.service';
import { LiteratureFilterStateManagerService } from './services/literature-filter-state-manager.service';
import { SegmentService } from '@services/segment.service';
import { DatadogService } from '@services/datadog.service';
import { StickyFooterService } from '@services/sticky-footer.service';
import { COLDEF } from '../../interactive-content/ucits-etf-data-table/types/model/indexes';
import { WINDOW } from '@ng-web-apis/common';

const logger = Logger.getLogger('LiteratureDocumentListingComponent');

@Component({
  selector: 'ft-literature-document-listing',
  templateUrl: './literature-document-listing.component.html',
  styleUrls: ['./literature-document-listing.component.scss'],
  providers: [
    LiteratureDocumentListingService,
    DatePipe,
    LiteratureFilterStateManagerService,
    FundFilterService,
  ],
})
export class LiteratureDocumentListingComponent
  extends LiteratureListingBaseComponent
  implements OnInit, OnDestroy, DownloadLink {
  @Input() component: BrComponent;
  @Input() page!: Page;
  paramMap: Map<string, string>;
  currentPage: LiteratureDocumentListingData[];
  searchKey = '';
  // number of total pages for pagination
  numberOfPages: number;
  // number of data after filter is applied
  numberOfFliterData = 0;
  dataLoaded: boolean;
  columnDefs = [];
  isInternational: boolean;
  isCanada: boolean;
  isLuxembourg: boolean;
  // to decidde the number of rows in ag-grid
  paginationPageSize: number;
  // total number of data without any filter
  totalNumberOfData: number;
  rowData: LiteratureDocumentListingData[] = [];
  customRowData: CustomLiteratureDocumentListingData[] = [];
  rowNoItems = [];
  sortByItems = [];
  rowsNoDropDownLabel: string;
  sortByDropDownLabel: string;
  isError: boolean;
  sortOn: string;
  ModalId = ModalId;
  clientUseToggle: ToggleButton[] = [];
  showClientToggle = false;
  pageSize;
  activePage: number;
  toSetPageFromStorage: number;
  selectedToggleValue = '';
  private unsubscribe$: Subject<void> = new Subject<void>();
  columnsForExcel: string[] = [];
  initialDataCount = 0;
  endDataCount = 50;
  permanentColumnsForExcel: string[] = [
    'fundName',
    'shareClassName',
    'shareClassCurrency',
  ];
  showDownloadExcel = false;
  public documentsWithPreview: string[];

  @ViewChild('literatureDocumentGrid', { static: false })
  literatureDocumentGrid: AgGridAngular;
  @ViewChild(PaginationComponent, { static: false })
  paginationComponent: PaginationComponent;
  gridContext = {
    filteredRows: null,
  };
  isLargeTabletAndSmallDesk: boolean;
  isMobile$: Observable<boolean>;
  href: string;
  isSelectAllPopupClicked: boolean;
  private currentSegment: SegmentId;
  isFP: boolean;
  isRequeryResult = false; // To show Requery message
  pageTitle: string;

  isDesktop: boolean;
  public isLargeDesktop: boolean;
  public isLoggedIn$: Observable<boolean>;
  public litOrderHistoryUrl: string;
  public emailPrefrenceLink: string;
  public rowHeight: string;
  public notificationOption: NotificationOptions = { primary: {} };

  public globalRegulatoryRowData: ShareclassLevelLiteratureDocumentListingData[] = [];
  public pageType: LiteraturePageType;
  public readonly LiteraturePageType = LiteraturePageType;
  public isRegulatoryPage: boolean;
  public isInitialLoad = true;
  public showSignInComponent = false;
  public isShareclassLevelRegulatory = false;
  constructor(
    private literatureDocumentListingService: LiteratureDocumentListingService,
    private literatureAgGridHelperService: LiteratureAgGridHelperService,
    private router: Router,
    protected changeDetectorRef: ChangeDetectorRef,
    protected responsiveService: ResponsiveService,
    protected translateService: TranslateService,
    protected downloadService: DownloadService,
    protected cartService: CartHandlerService,
    protected dialogService: DialogService,
    protected modalService: ModalService,
    protected formBuilder: FormBuilder,
    private filterStateService: LiteratureFilterStateManagerService,
    protected profileService: ProfileService,
    protected analyticsService: AnalyticsService,
    protected literatureConfig: LiteratureGlobalConfigService,
    private titleService: Title,
    private filterStateManagerService: LiteratureFilterStateManagerService,
    private storageService: StorageService,
    private scrollService: WindowScrollService,
    private datadogService: DatadogService,
    protected fundFilterService: FundFilterService,
    private segmentService: SegmentService,
    protected stickyFooterService: StickyFooterService,
    @Inject(DOCUMENT) private documentRef: Document,
    @Inject(WINDOW) readonly windowRef: Window
  ) {
    super(
      responsiveService,
      changeDetectorRef,
      cartService,
      dialogService,
      downloadService,
      translateService,
      formBuilder,
      literatureConfig,
      modalService,
      profileService,
      analyticsService,
      fundFilterService,
      stickyFooterService
    );
    this.isMobile$ = this.responsiveService.isMobile$();
    this.isLoggedIn$ = this.profileService.isLoggedIn$();
    // read current segment
    this.segmentService
      .getCurrentSegmentId$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((currentSegmentId: SegmentId) => {
        this.currentSegment = currentSegmentId;
        this.isFP = currentSegmentId === SegmentId.FINANCIAL_PROFESSIONALS;
      });
  }

  getDownloadUrl(url: string): string {
    return this.literatureDocumentListingService.getDownloadUrl(url);
  }

  ngOnInit() {
    logger.debug('listing loaded');
    this.notificationOption = {
      primary: {
        signInRequired: true,
        hideLockIcon: true,
      },
    };
    this.href = this.router.url;
    this.pageTitle = this.titleService.getTitle();
    this.pageType = this.component?.getModels()?.config?.litPageType;
    this.isShareclassLevelRegulatory = this.component?.getModels()?.config?.isShareclassLevelRegulatory;
    this.isRegulatoryPage =
      this.pageType === LiteraturePageType.REGULATORY_FUND_DOCUMENTS ||
      this.pageType === LiteraturePageType.REGULATORY_FUND_DOCUMENTS_GLOBAL;
    this.litOrderHistoryUrl = this.literatureConfig?.navigationUrls?.orderHistoryList;
    this.emailPrefrenceLink = this.literatureConfig?.navigationUrls?.manageEmailPreference;
    this.storageService.retrieve<number>('ToSetPage', true).then((data) => {
      this.toSetPageFromStorage = data;
      if (this.toSetPageFromStorage) {
        this.storageService
          .retrieve<number>('CurrentPage', true)
          .then((pageNumber) => {
            this.activePage = pageNumber;
          });
        this.storageService.store<number>('ToSetPage', 0, true);
      } else {
        this.activePage = 1;
      }
    });
    this.isInternational = this.literatureConfig.isInternational;
    this.isCanada = this.literatureConfig.isCanada;
    this.isLuxembourg = this.literatureConfig.isLuxembourg;
    this.literatureConfig.setLiteratureListingConfig({
      pageLayoutType: this.component?.getModels()?.config?.litPagelayout,
      pageType: this.component?.getModels()?.config?.litPageType,
    });
    this.rowHeight = this.calculateRowHeight();
    super.ngOnInit();
    this.clientUseToggle = [];
    this.clientUseToggle.push(
      {
        active: false,
        text: this.translateService.instant('literature.Yes'),
        value: 'Yes',
      },
      {
        active: true,
        text: this.translateService.instant('literature.No'),
        value: 'No',
      }
    );

    this.filterStateSubscription();

    this.paramMap = new Map();
    this.paramMap.set('cache', 'true');

    this.responsiveServiceSubscription();

    this.literatureDocumentListingService
      .register()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state) => {
        this.isError = state.isError;
        if (this.isHandheld) {
          // To avoid multiple time sorting as desktop view is already been sorted at ag-grid level.
          const sortedData = this.literatureDocumentListingService.sortDataByTicker(
            state.data,
            this.pageType
          );
          this.forHandHeldFilterData(sortedData);
        } else {
          this.forGridFilterData(state.data);
        }

        switch (this.pageType) {
          case LiteraturePageType.REGULATORY_FUND_DOCUMENTS:
            this.populateCustomLiteratureData(state.data);
            break;
          case LiteraturePageType.REGULATORY_FUND_DOCUMENTS_GLOBAL:
            this.populateGlobalRegulatoryFundDocument(state.data);
            break;
          default:
            this.populateLiteratureData(state.data);
        }
        this.showSignInComponent = this.getShowSignInComponent();
      });
    this.showDownloadExcel = this.component?.getModels()?.config?.showDownloadExcel;
    if (this.pageType !== LiteraturePageType.REGULATORY_FUND_DOCUMENTS_GLOBAL) {
      this.handlePagination();
    }
    this.literatureDocumentListingService.populate(
      this.paramMap,
      this.pageType,
      this.isShareclassLevelRegulatory
    );
    this.isCartConfigured =
      this.literatureDocumentListingService
        .getColumnToDisplay()
        ?.indexOf('ADD_TO_CART') !== -1;
    this.literatureDocumentListingService
      .getTotalNumberofData()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: number) => {
        this.totalNumberOfData = data;
      });
    this.filterStateService.subscribe((state: FiltersState) => {
      if (state) {
        this.searchKey = `${
          state.searchKeyword?.selected?.length > 0
            ? ' ["' + state.searchKeyword?.selected[0] + '"] '
            : ' '
        }`;
      }
    });
    this.rowsNoDropDownLabel = this.translateService.instant(
      'literature.lit-items-per-page'
    );
    this.sortByDropDownLabel = this.translateService.instant(
      'literature.lit-sort-by'
    );
    this.rowNoItems = this.createDropDownRowNoItems();
    this.sortByItems = this.createSortByDropDownItems();
  }

  private responsiveServiceSubscription() {
    this.responsiveService
      .getBreakpointObs$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((bp: FrkBreakpoint) => {
        if ([FrkBreakpoint.Mobile, FrkBreakpoint.Tablet].includes(bp)) {
          // This will be only called when user will shift in different device whithout loading the page.
          if (this.rowData?.length) {
            const sortedData = this.literatureDocumentListingService.sortDataByTicker(
              this.rowData,
              this.pageType
            );
            this.rowData = sortedData;
          }
        }
        if ([FrkBreakpoint.LargeDesktop].includes(bp)) {
          this.isLargeDesktop = true;
        }
        if ([FrkBreakpoint.Desktop].includes(bp)) {
          this.isDesktop = true;
        }
        if ([FrkBreakpoint.LargeTablet].includes(bp)) {
          this.isLargeTabletAndSmallDesk = true;
          // this will be only called when user will shift to large tablet and small desktop devices.
          this.columnDefs = this.getColumnDefs(true).filter(
            (coldef: ColDef) => {
              return (
                this.literatureDocumentListingService
                  .getColumnToDisplay()
                  ?.indexOf(coldef.colId) !== -1
              );
            }
          );
        }
      });
  }

  private filterStateSubscription() {
    this.filterStateManagerService
      .getFilterState()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((state: FiltersState) => {
        if (state.clientUseFilter?.selected?.length) {
          this.selectedToggleValue = state.clientUseFilter.selected[0];
        } else {
          this.selectedToggleValue = 'No';
        }
        this.setActive();
        this.isRequeryResult = state.lastAction === 'requery';
        this.changeDetectorRef.detectChanges();
      });
  }

  private setActive() {
    this.clientUseToggle.forEach((toggleOption) => {
      if (toggleOption.value === this.selectedToggleValue) {
        toggleOption.active = true;
      } else {
        toggleOption.active = false;
      }
    });
  }

  mapDataHeader(columnsForExcel: string[]): Map<string, string> {
    const mappedHeader = new Map<string, string>();
    columnsForExcel.forEach((column) => {
      mappedHeader.set(column, `literature.${column}`);
    });
    return mappedHeader;
  }

  private calculateRowHeight(): string {
    let rowHeight;
    if (this.isInternational) {
      rowHeight = '124';
    } else {
      rowHeight = this.isLargeDesktop ? '146' : '176';
    }
    return rowHeight;
  }

  sortRowData(
    totalRecord: Map<string, ExcelDocType[]>
  ): Map<string, ExcelDocType[]> {
    // sorted based on keys
    const sortedMap: Map<string, ExcelDocType[]> = new Map(
      [...totalRecord.entries()].sort()
    );
    return sortedMap;
  }

  downloadExcel(): void {
    if (this.pageType === LiteraturePageType.REGULATORY_FUND_DOCUMENTS_GLOBAL) {
      this.literatureDocumentListingService.regulatoryExcelDownload();
    } else {
      this.columnsForExcel = [
        ...this.literatureConfig.getLiteratureDownloadExcelIdentifiers(),
        ...this.permanentColumnsForExcel,
        ...this.literatureDocumentListingService.documentsAvailableForExcel,
      ];
      const mappedHeader = this.mapDataHeader(this.columnsForExcel);
      const mappedBody = this.sortRowData(
        this.mapFundlevelDatawithShareclassLevelData(
          this.literatureDocumentListingService.excelDataMap
        )
      );
      const mappedFullRecords = [
        this.columnsForExcel.map((data) =>
          this.translateService.instant(mappedHeader.get(data))
        ),
      ];

      mappedBody.forEach((data) => {
        const temp: string[] = [];

        this.columnsForExcel.forEach((col) => {
          let flag = false;
          data.forEach((el) => {
            // flag is used to restrict same document with multiple lang.
            if (el.columnHeader === col && !flag) {
              flag = true;
              temp.push(el.columnValue);
            }
          });
          if (!flag) {
            temp.push('-');
          }
        });
        mappedFullRecords.push(temp);
      });

      this.exportToExcel(mappedFullRecords);
    }
  }

  exportToExcel(mappedFullRecords: string[][]): void {
    import('xlsx').then((xlsx) => {
      const workSheet = xlsx.utils.aoa_to_sheet(mappedFullRecords);

      const workBook = xlsx.utils.book_new();
      const fileNmae = this.translateService.instant(
        'literature.excel-download-filename'
      );

      xlsx.utils.book_append_sheet(workBook, workSheet);

      xlsx.writeFile(workBook, fileNmae);
    });
  }

  mapFundlevelDatawithShareclassLevelData(
    data: Map<string, ExcelDocType[]>
  ): Map<string, ExcelDocType[]> {
    data.forEach((value, key) => {
      let tempObj;
      const tempFundID = value.filter((item) => {
        return item.columnHeader === 'fundId';
      })[0].columnValue;
      if (
        this.literatureDocumentListingService.excelFundLevelData[tempFundID]
      ) {
        tempObj = [
          ...value,
          ...this.literatureDocumentListingService.excelFundLevelData[
            tempFundID
          ],
        ];
        data.set(key, tempObj);
      } else {
        data.set(key, value);
      }
    });
    return data;
  }

  public filterOnClientUse(selectedToggleOption: string): void {
    const val = selectedToggleOption === 'on' ? 'Yes' : 'No';
    this.filterStateService.filterByClientUse(val);
  }

  forHandHeldFilterData(data) {
    this.rowData = data;
    if (this.sortOn) {
      this.sortData(this.rowData, this.sortOn);
    }
  }

  forGridFilterData(data) {
    this.gridContext.filteredRows = data;
    this.literatureDocumentGrid?.api.onFilterChanged();
  }
  /**
   * populates literature documents into the grid
   * @param data LiteratureDocumentListingData
   */
  private populateLiteratureData(data: LiteratureDocumentListingData[]) {
    this.columnDefs = this.getColumnDefs().filter((coldef: ColDef) => {
      return (
        this.literatureDocumentListingService
          .getColumnToDisplay()
          ?.indexOf(coldef.colId) !== -1
      );
    });
    this.dataLoaded = this.literatureDocumentListingService.dataInitialised;
    this.rowData = data;
    this.numberOfFliterData = data.length;
    this.showClientToggle = this.literatureConfig.IsUserLoggedInNotShareHolder();
    this.documentsWithPreview =
      this.literatureConfig?.literatureConfig?.documentsWithPreview || [];

    this.changeDetectorRef.detectChanges();

    if (this.numberOfFliterData === 0) {
      this.trackZeroResults();
    }
  }

  private populateGlobalRegulatoryFundDocument(
    data: ShareclassLevelLiteratureDocumentListingData[]
  ) {
    this.columnDefs = this.getGlobalRegulatoryColumnDef(
      this.literatureDocumentListingService.getDocumentToDisplay()
    );
    this.dataLoaded = this.literatureDocumentListingService.dataInitialised;

    this.globalRegulatoryRowData = data;
    // create excel data if configured from BR
    if (this.isInitialLoad && this.showDownloadExcel) {
      this.isInitialLoad = false;
      this.literatureDocumentListingService
        .prepareShareClassLevelExcelData(data, this.columnDefs)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(() => {
          logger.debug('regulatory excel created');
        });
    }
    this.numberOfFliterData = data?.length;
    this.currentPage = this.globalRegulatoryRowData;
    this.changeDetectorRef.detectChanges();
  }

  private trackZeroResults(): void {
    // datadog tracking for not found terms in literature search
    const filtersObj = this.fundFilterService?.getCurrentFilterSelection()
      ?.selectedFilters;

    let searchTerm = '';

    const searchTermFilter = [...filtersObj.entries()].filter(
      (entry) => entry[0].key === 'SEARCH_KEYWORD'
    );

    if (searchTermFilter.length) {
      searchTerm = searchTermFilter[0][1][0];
    }

    // we don't need to track if no searhc term added / only filter dropdowns set
    if (!searchTerm) {
      return;
    }

    const numberOfFiltersSet = [...filtersObj.entries()].length;

    const currentSegment = this.currentSegment;
    const trackingObjFlat: {
      searchTerm: string;
      numberOfFiltersSet: number;
      currentSegment: SegmentId;
    } = { searchTerm, numberOfFiltersSet, currentSegment };

    // track not found in datadog - 20% session is tracked
    this.datadogService.addAction(
      'literature listing - not found',
      trackingObjFlat
    );

    // additional tracking to GA to compare results - only users who accepted cookies
    this.analyticsService.trackEvent({
      event: 'form_filter',
      name: 'Keyword Filter',
      label:
        'no results | ' +
        searchTerm +
        ' | ' +
        numberOfFiltersSet +
        ' | ' +
        this.currentSegment,
    });
  }

  private populateCustomLiteratureData(data: Array<any>) {
    // to get column def from document type and column to display
    const customColumnDef = this.literatureDocumentListingService.getColumnToDisplay();
    this.literatureConfig
      .getLiteratureListingConfig()
      ?.documentTypeDisplay.forEach((dataType) => {
        customColumnDef.push(dataType);
      });
    this.columnDefs = this.getCustomColumnDef().filter((coldef: ColDef) => {
      if (customColumnDef?.indexOf(coldef.colId) !== -1) {
        return true;
      } else {
        return false;
      }
    });
    this.dataLoaded = this.literatureDocumentListingService.dataInitialised;
    this.documentsWithPreview =
      this.literatureConfig?.literatureConfig?.documentsWithPreview || [];

    this.customRowData = data;
    if (this.isRegulatoryPage) {
      this.customRowData = data.sort((a, b) => {
        return a.product.fundName.localeCompare(b.product.fundName);
      });
    }

    this.numberOfFliterData = data?.length;
    this.currentPage = this.customRowData;
    this.changeDetectorRef.detectChanges();
  }
  createDropDownRowNoItems() {
    const dropDownRows: string[] = this.translateService
      .instant('literature.lit-listing-number-of-rows')
      .split(',');
    let dropDownRowsNoItems = [];
    if (dropDownRows) {
      dropDownRowsNoItems = dropDownRows.map((item) => {
        return { value: item, label: `${item}` };
      });
    }
    return dropDownRowsNoItems;
  }

  getSortByItemsDef() {
    return this.getColumnDefs().filter((colItem) => {
      return (
        this.literatureDocumentListingService
          .getColumnToDisplay()
          ?.indexOf(colItem.colId) !== -1 &&
        (colItem.colId === 'NAME' ||
          colItem.colId === 'DETAIL' ||
          colItem.colId === 'PUBLICATION_DATE')
      );
    });
  }
  createSortByDropDownItems() {
    let sortByItemValues = [];
    const sortByItemsDef = this.getSortByItemsDef();
    sortByItemValues = sortByItemsDef.map((colDefItem) => {
      return {
        value: colDefItem.colId,
        label: colDefItem.headerName,
      };
    });
    return sortByItemValues;
  }

  private handlePagination() {
    this.paginationPageSize = this.literatureDocumentListingService.getPageSize();
    this.literatureDocumentListingService
      .getNumberOfPages$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data) => {
        this.numberOfPages = data;
        this.paginationComponent?.updatePagination(this.numberOfPages);
        this.pageSize = this.rowNoItems.find(
          (options) =>
            Number(options.label) ===
            this.literatureDocumentListingService.getPageSize()
        );
        if (this.pageSize) {
          this.gridPageSize = parseInt(this.pageSize.value, 10);
        }
        this.changeDetectorRef.detectChanges();
      });
    this.literatureDocumentListingService
      .getCurrentPage$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((pageData: LiteratureDocumentListingData[]): void => {
        this.currentPage = pageData;
        this.changeDetectorRef.detectChanges();
      });
    this.literatureDocumentListingService
      .getPageNumber$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((n: number): void => {
        this.literatureDocumentGrid?.api.paginationGoToPage(n - 1);
      });
  }

  onPageChanged(event: any) {
    if (event.action !== 'load') {
      if (this.selectedNodes?.length <= this.maxSelectionLimit) {
        this.gridApi?.refreshHeader();
      }

      this.scrollService.scrollToAnchor('literatureScrollPosition');
    }
    this.storageService.store<number>('CurrentPage', event.page, true);
    this.literatureDocumentListingService.goToPage(event.page);
  }

  sortData(
    data: LiteratureDocumentListingData[],
    selectedValue: string
  ): LiteratureDocumentListingData[] {
    if (selectedValue === 'DETAIL') {
      return data.sort((current, next) => {
        return detailsGenerator(
          current,
          this.translateService,
          this.isInternational
        ) < detailsGenerator(next, this.translateService, this.isInternational)
          ? -1
          : 1;
      });
    } else if (selectedValue === 'NAME') {
      const nameCompare = stringCompare;
      return data.sort((current, next) => {
        return nameCompare(
          this.isInternational
            ? nameGenerator(current, this.translateService)
            : fundNameGenerator(current, this.translateService),
          this.isInternational
            ? nameGenerator(next, this.translateService)
            : fundNameGenerator(next, this.translateService),
          null,
          null,
          false
        );
      });
    } else if (selectedValue === 'PUBLICATION_DATE') {
      const dateCompare = createDateComparator(this.apiDateFormat);
      return data
        .sort((current, next) => {
          return dateCompare(
            current.literatureData.frkReferenceDate,
            next.literatureData.frkReferenceDate,
            null,
            null,
            false
          );
        })
        .reverse();
    }
  }

  dateGenerator(data: LiteratureDocumentListingData): Date {
    if (data.literatureData.frkReferenceDate) {
      const date = data.literatureData.frkReferenceDate.split('-');
      return new Date(Number(date[0]), Number(date[1]) - 1, Number(date[2]));
    }
    return;
  }

  onSorting(data: LiteratureDocumentListingData[], event) {
    this.sortOn = event.value;
    this.rowData = this.sortData(data, event.value);
    this.literatureDocumentListingService.pagedData.records = this.rowData;
  }

  onChange(numberOfRowsEvent) {
    this.literatureDocumentListingService.updateNumberOfRows(
      Number(numberOfRowsEvent.value)
    );
    this.literatureDocumentGrid?.api?.paginationSetPageSize(
      Number(numberOfRowsEvent.value)
    );
  }

  isExternalFilterPresent = () => true;

  doesExternalFilterPass(node: any) {
    if (node.gridOptionsWrapper.getContext().filteredRows) {
      return (
        node.gridOptionsWrapper.getContext().filteredRows?.indexOf(node.data) >
        -1
      );
    }
    return true;
  }

  postEmailDocs(
    recipients: string[],
    litCodes: string[],
    showStatus?: (statusMessage: string) => void
  ): void {
    this.literatureDocumentListingService.postEmailDocs(
      recipients,
      litCodes,
      showStatus
    );
  }

  private getColumnDefs(isLargeTabletAndSmallDesk?: boolean): ColDef[] {
    return this.literatureAgGridHelperService.getDocumentListingColDef(
      this.isInternational,
      this.literatureDocumentListingService.isInvestor(),
      this.literatureConfig.navigationUrls?.litDetailUrl,
      this.apiDateFormat,
      this,
      this.literatureDocumentListingService.dateFormat,
      this.isCanada,
      this.isLuxembourg,
      this.isLargeTabletAndSmallDesk || isLargeTabletAndSmallDesk,
      this.isLargeDesktop
    );
  }

  private getCustomColumnDef(): ColDef[] {
    // this change is in case literature config is missing in BR.
    // we set empty array if there is no option
    this.documentsWithPreview =
      this.literatureConfig?.literatureConfig?.documentsWithPreview || [];
    return this.literatureAgGridHelperService.getCustomDocumentListingColDef(
      this.isInternational,
      this.documentsWithPreview,
      this.pageType !== LiteraturePageType.REGULATORY_FUND_DOCUMENTS,
      this.isShareclassLevelRegulatory
    );
  }

  private getGlobalRegulatoryColumnDef(
    colData: string[]
  ): DynamicTableColDef<COLDEF>[] {
    const colDefs: DynamicTableColDef<COLDEF>[] = [];
    colDefs.push(
      {
        field: 'fundName',
        headerName: this.translateService.instant('literature.fund-name'),
        isCardTitle: true,
        sortable: true,
      },
      {
        field: 'shareClass',
        headerName: this.translateService.instant('literature.share-class'),
      }
    );
    colData.forEach((col) => {
      colDefs.push({
        field: col,
        headerName: this.translateService.instant(this.getColumnHeading(col)),
      });
    });

    colDefs.push({
      field: 'additionalDocuments',
      headerName: this.translateService.instant(
        'literature.additional-documents'
      ),
    });

    return colDefs;
  }

  // TODO: check the key genration logic .toLowerCase().replace(/ /g, '-');
  private getColumnHeading(colId: string): string {
    return 'literature.' + colId.trim();
  }

  // Events Capturing here for Select All and single row selection
  selectRow(event, isFromSelectAll: boolean, isSelect: boolean) {
    if (isFromSelectAll) {
      this.isSelectAllPopupClicked = !isSelect;
    }
    this.onListingSelectionChanged(event, this.href, isFromSelectAll, isSelect);
  }

  selectAllPopupAction(event: SelectNotifications): void {
    this.isSelectAllPopupClicked = true;
    if (event === SelectNotifications.ALL) {
      this.addListingSelectedRows(this.remainingNonSelectedRows);
    } else {
      this.addListingSelectedRows(this.currentPageNonSelectedRows);
    }
  }

  modalClosedInternally(event: string) {
    if (
      this.ModalIdValue === ModalId.Selection_All_Modal &&
      !this.isSelectAllPopupClicked
    ) {
      this.gridApi.refreshHeader();
    }
  }

  /**
   * Conditions required to show sign in component
   * @returns - boolean
   */
  private getShowSignInComponent = () =>
    !this.isRegulatoryPage &&
    !this.isInternational &&
    this.isFP &&
    this.dataLoaded;

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.filterStateManagerService?.unsubscribe();
  }
}
